import { courseServiceApi } from "../../apis/courseService.api";
import { COURSE_API_TAGS } from "../../app.constants";
import type { IAddOrderDto } from "../order/order.types";

import type {
  GetCourseReview,
  GetMyCourseReview,
  GetPromotionParams,
  GetPromotionResponse,
  GetVerifyCertificate,
  IAddOrderResponse,
  ICategoryResponse,
  ICertificateResponse,
  ICourseEnrollDto,
  ICourseParams,
  ICourseProgressDto,
  ICourseProgressResponse,
  ICourseResponse,
  ICourseReviewDto,
  ICourseSummeryResponse,
  ICourseValidateDto,
  ICourseValidateResponse,
  ICurriculumResponse,
  ICurriculumWithProgressResponse,
  ILecturesResponse,
  IPublicCourseListResponse,
  IPublicCourseParams,
  IPublicCourseResponse,
  IPublicReviewParams,
  IPublicReviewResponse,
  IQuizDetailsResponse,
  IQuizResultResponse,
  IQuizStatusParams,
  IQuizStatusResponse,
  IQuizSubmitDto,
  IQuizSubmitResponse,
  ISingleQuizSubmitDto,
  ISingleQuizSubmitResponse,
} from "./courses.types";

const PUBLIC_COURSE_URL = "/public/courses";
const ME_COURSE_URL = "/me/courses";

export const courseApi = courseServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // --------------------
    // Public Courses
    // --------------------
    getCoursesCategory: builder.query<ICategoryResponse[], void>({
      query: () => ({
        url: `${PUBLIC_COURSE_URL}/categories`,
        method: "GET",
      }),
    }),
    getPublicCourses: builder.query<
      IPublicCourseListResponse,
      IPublicCourseParams
    >({
      query: (params) => ({
        url: PUBLIC_COURSE_URL,
        method: "GET",
        params,
      }),
      providesTags: [COURSE_API_TAGS.PUBLIC_COURSE],
    }),
    getCoursesReviews: builder.query<
      IPublicReviewResponse,
      IPublicReviewParams
    >({
      query: (params) => ({
        url: `${PUBLIC_COURSE_URL}/reviews`,
        method: "GET",
        params,
      }),
      // providesTags: [COURSE_API_TAGS.PUBLIC_COURSE],
    }),

    // --------------------
    // Public COURSE DETAILS
    // --------------------
    getPublicCourseDetails: builder.query<IPublicCourseResponse, string>({
      query: (slug) => ({
        url: `${PUBLIC_COURSE_URL}/${slug}`,
        method: "GET",
      }),
      providesTags: (_r, _e, slug) => [
        { type: COURSE_API_TAGS.PUBLIC_COURSE, slug },
      ],
    }),

    // --------------------
    // Courses
    // --------------------
    getCourses: builder.query<ICourseResponse[], ICourseParams>({
      query: (params) => ({
        url: ME_COURSE_URL,
        method: "GET",
        params,
      }),
      providesTags: [COURSE_API_TAGS.COURSE],
    }),
    getCoursesSummery: builder.query<ICourseSummeryResponse, void>({
      query: () => ({
        url: `${ME_COURSE_URL}/summary`,
        method: "GET",
      }),
      providesTags: [COURSE_API_TAGS.COURSE],
    }),

    // --------------------
    // Recommended Courses
    // --------------------
    getRecommendedCourses: builder.query<ICourseResponse[], void>({
      query: () => ({
        url: `${ME_COURSE_URL}/recommendations`,
        method: "GET",
      }),
      providesTags: [COURSE_API_TAGS.RECOMMENDATION],
    }),

    // --------------------
    // Curriculum
    // --------------------
    getCourseCurriculum: builder.query<ICurriculumResponse, string>({
      query: (id) => ({
        url: `${ME_COURSE_URL}/${id}/curriculum`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.COURSE, id }],
    }),
    getCourseCurriculumWithProgress: builder.query<
      ICurriculumWithProgressResponse,
      string
    >({
      query: (id) => ({
        url: `${ME_COURSE_URL}/${id}/curriculum-with-progress`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.COURSE, id }],
    }),

    // --------------------
    // Lectures
    // --------------------
    getCourseLectures: builder.query<
      ILecturesResponse,
      { cId: string; lId: string }
    >({
      query: ({ cId, lId }) => ({
        url: `${ME_COURSE_URL}/${cId}/lectures/${lId}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { cId }) => [
        { type: COURSE_API_TAGS.COURSE, id: cId },
      ],
    }),

    // --------------------
    // Quiz Details
    // --------------------
    getCourseQuiz: builder.query<
      IQuizDetailsResponse[],
      { cId: string; qId: string }
    >({
      query: ({ cId, qId }) => ({
        url: `${ME_COURSE_URL}/${cId}/quizzes/${qId}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { cId }) => [
        { type: COURSE_API_TAGS.COURSE, id: cId },
      ],
    }),

    // --------------------
    // Certificate
    // --------------------
    getCertificate: builder.query<ICertificateResponse, string>({
      query: (id) => ({
        url: `${ME_COURSE_URL}/${id}/certificate`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.COURSE, id }],
    }),

    // --------------------
    // Course Progress
    // --------------------
    courseProgress: builder.mutation<void, ICourseProgressDto>({
      query: ({ courseId, ...rest }) => ({
        url: `${ME_COURSE_URL}/${courseId}/progress`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),
    getCourseProgress: builder.query<ICourseProgressResponse, number>({
      query: (courseId) => ({
        url: `${ME_COURSE_URL}/${courseId}/progress`,
        method: "GET",
      }),
      providesTags: (_r, _e, courseId) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),

    // --------------------
    // Quiz Submit
    // --------------------
    getQuizAnswer: builder.mutation<
      ISingleQuizSubmitResponse,
      ISingleQuizSubmitDto
    >({
      query: ({ courseId, quizId, questionId, ...rest }) => ({
        url: `${ME_COURSE_URL}/${courseId}/quizzes/${quizId}/questions/${questionId}/answer`,
        method: "POST",
        body: rest,
      }),
    }),
    quizSubmit: builder.mutation<IQuizSubmitResponse, IQuizSubmitDto>({
      query: ({ courseId, quizId, ...rest }) => ({
        url: `${ME_COURSE_URL}/${courseId}/quizzes/${quizId}/submit`,
        method: "POST",
        body: { ...rest, quizId },
      }),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),
    getQuizStatus: builder.query<IQuizStatusResponse, IQuizStatusParams>({
      query: ({ courseId, quizId }) => ({
        url: `${ME_COURSE_URL}/${courseId}/quizzes/${quizId}/status`,
        method: "GET",
      }),
      providesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),
    getQuizResult: builder.query<IQuizResultResponse, IQuizStatusParams>({
      query: ({ courseId, quizId }) => ({
        url: `${ME_COURSE_URL}/${courseId}/quizzes/${quizId}/result`,
        method: "GET",
      }),
      providesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),

    // --------------------
    // Course Review
    // --------------------
    courseReview: builder.mutation<void, ICourseReviewDto>({
      query: ({ courseId, ...rest }) => ({
        url: `${ME_COURSE_URL}/${courseId}/review`,
        method: "POST",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),
    getCourseReview: builder.query<GetCourseReview, number>({
      query: (courseId) => ({
        url: `${ME_COURSE_URL}/${courseId}/review`,
        method: "GET",
      }),
      providesTags: (_r, _e, courseId) => [
        { type: COURSE_API_TAGS.COURSE, id: courseId },
      ],
    }),
    getMyReview: builder.query<GetMyCourseReview, void>({
      query: () => ({
        url: `${ME_COURSE_URL}/my-reviews`,
        method: "GET",
      }),
      providesTags: (_r, _e) => [{ type: COURSE_API_TAGS.COURSE }],
    }),

    // --------------------
    // Enroll Course
    // --------------------
    enrollCourse: builder.mutation<void, ICourseEnrollDto>({
      query: (body) => ({
        url: `${ME_COURSE_URL}/enroll`,
        method: "POST",
        body,
      }),
      invalidatesTags: [COURSE_API_TAGS.COURSE, COURSE_API_TAGS.RECOMMENDATION],
    }),
    addOrder: builder.mutation<IAddOrderResponse, IAddOrderDto>({
      query: (body) => ({
        url: "/me/orders",
        method: "POST",
        body,
      }),
      // invalidatesTags: [ORDER_API_TAGS.ORDER],
    }),
    verifyCertificate: builder.query<
      GetVerifyCertificate,
      { certificateNo: string }
    >({
      query: (params) => ({
        url: `${PUBLIC_COURSE_URL}/verify-certificate`,
        method: "GET",
        params,
      }),
      providesTags: (_r, _e) => [{ type: COURSE_API_TAGS.COURSE }],
    }),

    courseValidate: builder.mutation<
      ICourseValidateResponse,
      ICourseValidateDto
    >({
      query: (body) => ({
        url: `${PUBLIC_COURSE_URL}/validate`,
        method: "POST",
        body,
      }),
    }),

    getPromotions: builder.query<GetPromotionResponse, GetPromotionParams>({
      query: (params) => ({
        url: `${PUBLIC_COURSE_URL}/get/promotions`,
        method: "GET",
        params,
        headers: {
          "Cache-Control": "no-cache",
        },
      }),
    }),
  }),
});

export const {
  useGetCoursesCategoryQuery,
  useGetCertificateQuery,
  useGetPublicCoursesQuery,
  useGetCoursesReviewsQuery,
  useGetPublicCourseDetailsQuery,
  useGetCoursesQuery,
  useGetCoursesSummeryQuery,
  useGetRecommendedCoursesQuery,
  useGetCourseCurriculumQuery,
  useGetCourseCurriculumWithProgressQuery,
  useGetCourseLecturesQuery,
  useGetCourseQuizQuery,
  useCourseProgressMutation,
  useGetCourseProgressQuery,
  useGetQuizAnswerMutation,
  useGetQuizStatusQuery,
  useGetQuizResultQuery,
  useQuizSubmitMutation,
  useEnrollCourseMutation,
  useCourseReviewMutation,
  useGetCourseReviewQuery,
  useGetMyReviewQuery,
  useAddOrderMutation,
  useVerifyCertificateQuery,
  useCourseValidateMutation,
  useGetPromotionsQuery,
} = courseApi;
